import { Avatar, Text } from '@mantine/core';
import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CopilotMessageItem } from 'app/modules/wallforming/components/copilot/CopilotMessageItem';
import engCopilot from 'shared/components/assets/engCopilot.jpg';

import type {
    CopilotMessage,
    CopilotMode,
    CopilotProposal
} from 'app/modules/wallforming/components/copilot/types/copilotTypes';

interface CopilotMessageListProps {
    activeConversationId?: string;
    imageSmall?: string;
    isApplying: boolean;
    isSending: boolean;
    messages: CopilotMessage[];
    mode: CopilotMode;
    onApplyProposal: (proposal: CopilotProposal) => void;
    onCopyMessage: (content: string, renderedContent?: HTMLElement | null) => void;
    onRejectProposal: (proposalId: string) => void;
}

export const CopilotMessageList = memo(function CopilotMessageList({
    activeConversationId,
    imageSmall,
    isApplying,
    isSending,
    messages,
    mode,
    onApplyProposal,
    onCopyMessage,
    onRejectProposal
}: CopilotMessageListProps) {
    const { t } = useTranslation();
    const chatContentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const chatContent = chatContentRef.current;
        if (chatContent) {
            chatContent.scrollTop = chatContent.scrollHeight;
        }
    }, [activeConversationId, isSending, messages.length]);

    return (
        <div ref={chatContentRef} className="mb-4 mt-4 min-h-0 flex-1 overflow-y-auto bg-background-default px-3 py-4">
            <div aria-live="polite" className="flex flex-col gap-4">
                {messages.length === 0 && (
                    <div className="mx-auto my-auto max-w-sm px-4 py-10 text-center">
                        <img
                            alt=""
                            className="mx-auto mb-4 h-auto max-h-48 max-w-full object-contain"
                            height={192}
                            src={engCopilot}
                            width={128}
                        />
                        <Text className="text-md" fw={600}>
                            {t('copilotWelcome')}
                        </Text>
                        <Text c="dimmed" className="text-sm" mt={4}>
                            {t('copilotWelcomeDescription')}
                        </Text>
                    </div>
                )}
                {messages.map((message, index) => (
                    <CopilotMessageItem
                        key={`${message.role}-${index}`}
                        imageSmall={imageSmall}
                        isApplying={isApplying}
                        isSending={isSending}
                        message={message}
                        onApplyProposal={onApplyProposal}
                        onCopyMessage={onCopyMessage}
                        onRejectProposal={onRejectProposal}
                    />
                ))}
                {isSending && (
                    <div className="flex items-center gap-2 text-text-placeholder" role="status">
                        <Avatar aria-hidden="true" className="h-7 w-7 shrink-0" src={engCopilot} />
                        <Text c="dimmed" size="xs">
                            {t(mode === 'agent' ? 'copilotAgentThinking' : 'copilotAskThinking')}
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
});
